import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  IconButton,
} from "@mui/material";

import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { apipostFormData } from "../../service/api";


const validationSchema = yup.object({
  name: yup.string().required("State name is required"),
  code: yup
    .string()
    .max(2, "Use 2-letter abbreviation (e.g., FL)")
    .uppercase()
    .required("State code is required"),
});

export default function StateCreateForm() {
  const [stateImageFile, setStateImageFile] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      code: "",
      additionalOptions: [""],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();

        // Append text fields
        formData.append("name", values.name);
        formData.append("code", values.code.toUpperCase());

        // Additional options
        values.additionalOptions
          .filter((opt) => opt.trim() !== "")
          .forEach((opt, idx) => {
            formData.append(`additionalOptions[${idx}]`, opt);
          });

        // File upload
        if (stateImageFile) {
          formData.append("branchImage", stateImageFile);
        }

        const result = await apipostFormData("states", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (result?.status === 201) {
          toast.success("State created successfully!");
          resetForm();
          setStateImageFile(null);
        } else {
          toast.error(result?.data?.message || "Error creating state");
        }
      } catch (err) {
        console.error(err);
        toast.error("Unexpected error submitting form.");
      }
    },
  });

  // Dynamic additional options handlers
  const handleChangeOption = (index, value) => {
    const updated = [...formik.values.additionalOptions];
    updated[index] = value;
    formik.setFieldValue("additionalOptions", updated);
  };

  const handleAddOption = () => {
    formik.setFieldValue("additionalOptions", [
      ...formik.values.additionalOptions,
      "",
    ]);
  };

  const handleRemoveOption = (index) => {
    const updated = formik.values.additionalOptions.filter(
      (_, i) => i !== index
    );
    formik.setFieldValue("additionalOptions", updated);
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: "auto", mt: 8 }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Create New State
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {/* State Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="name"
              label="State Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>

          {/* State Code */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="code"
              label="State Code (ex: FL)"
              value={formik.values.code}
              onChange={(e) =>
                formik.setFieldValue("code", e.target.value.toUpperCase())
              }
              error={formik.touched.code && Boolean(formik.errors.code)}
              helperText={formik.touched.code && formik.errors.code}
            />
          </Grid>

          {/* Image Upload */}
          <Grid item xs={12}>
            <Typography variant="subtitle2">Upload State Image</Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setStateImageFile(e.target.files[0])}
            />
          </Grid>

          {/* Additional Options */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              State Additional Options (Optional)
            </Typography>

            {formik.values.additionalOptions.map((opt, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  label={`Option #${index + 1}`}
                  value={opt}
                  onChange={(e) => handleChangeOption(index, e.target.value)}
                />

                <IconButton
                  color="error"
                  onClick={() => handleRemoveOption(index)}
                  disabled={formik.values.additionalOptions.length === 1}
                >
                  <RemoveIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddOption}
              sx={{ mt: 1 }}
            >
              Add Another Option
            </Button>
          </Grid>

          {/* Submit */}
          <Grid item xs={12}>
            <Button type="submit" variant="contained" fullWidth>
              Create State
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
}
